import { type FunctionComponent, cloneElement, useState } from 'react';
import {
  EuiButton,
  EuiButtonEmpty,
  EuiComboBox,
  type EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutHeader,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiForm,
  EuiFormRow,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
  EuiSpacer,
  EuiToolTip,
} from '@elastic/eui';
import { useAddGeorole } from '@data/georole-mutations';
import useRoleNames from '@data/georoles';
import type { Person } from '@domain/person';
import type { StructureWithParents } from '@lib/structures';
import { compareStrings } from '@lib/utils';
import StructureSearch from '@components/structure-search';

interface AddGeoroleProps {
  person: Person;
  roleName?: string;
  button: JSX.Element;
  tooltip: string;
}

type GeoroleOption = EuiComboBoxOptionOption<string>;

const georoleOption = (roleName: string): GeoroleOption => ({
  label: roleName,
  value: roleName,
});

const AddGeorole: FunctionComponent<AddGeoroleProps> = props => {
  const { button, tooltip, roleName, person } = props;

  const { roleNames: roleNames = [] } = useRoleNames();
  const sortedRoles = [...roleNames].sort(compareStrings);

  const [addRole, { loading }] = useAddGeorole();

  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    roleName != null ? [roleName] : []
  );

  const [selectedStructures, setSelectedStructures] = useState<
    StructureWithParents[]
  >([]);

  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);

  const openFlyout = () => {
    setIsFlyoutVisible(true);
  };

  const closeFlyout = () => {
    setIsFlyoutVisible(false);
  };

  const handleClick = () => {
    Promise.all(
      selectedRoles.map(roleName =>
        addRole(person.idnumber, roleName, selectedStructures)
      )
    ).then(() => {
      closeFlyout();
    });
  };

  const titleId = useGeneratedHtmlId({ prefix: 'add-georole' });

  return (
    <>
      <EuiToolTip content={tooltip}>
        {cloneElement(button, {
          onClick: openFlyout,
          isLoading: loading,
          'aria-label': tooltip,
        })}
      </EuiToolTip>
      {isFlyoutVisible && (
        <EuiFlyout
          onClose={closeFlyout}
          ownFocus
          size="s"
          aria-labelledby={titleId}>
          <EuiFlyoutHeader hasBorder>
            <EuiTitle size="m">
              <h3 id={titleId}>Grant georoles</h3>
            </EuiTitle>
          </EuiFlyoutHeader>
          <EuiFlyoutBody>
            <EuiText>
              <p>
                Granting georole permissions to {person.firstname}{' '}
                {person.surname}
              </p>
            </EuiText>
            <EuiSpacer size="m" />
            <EuiForm>
              <EuiFormRow label="Georoles">
                <EuiComboBox
                  fullWidth
                  placeholder="Select georole(s)"
                  aria-label="Select georole(s)"
                  selectedOptions={selectedRoles.map(georoleOption)}
                  options={sortedRoles.map(georoleOption)}
                  onChange={options => {
                    setSelectedRoles(
                      options.map(option => option.value as string)
                    );
                  }}
                  rowHeight={50}
                  isClearable={true}
                  isDisabled={loading || roleName != null}
                />
              </EuiFormRow>

              <EuiFormRow label="Structures">
                <StructureSearch
                  values={selectedStructures}
                  onChange={setSelectedStructures}
                  disabled={loading}
                />
              </EuiFormRow>
            </EuiForm>
          </EuiFlyoutBody>
          <EuiFlyoutFooter>
            <EuiFlexGroup justifyContent="spaceBetween">
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  iconType="cross"
                  onClick={closeFlyout}
                  flush="left"
                  isLoading={loading}>
                  Cancel
                </EuiButtonEmpty>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton
                  iconType="plus"
                  onClick={handleClick}
                  fill
                  isDisabled={
                    selectedRoles.length === 0 ||
                    selectedStructures.length === 0
                  }
                  isLoading={loading}>
                  Add
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlyoutFooter>
        </EuiFlyout>
      )}
    </>
  );
};

export default AddGeorole;
